import { ExperimentBase, ExperimentConfig } from "./experiment-base";
import { LLMWrapper, ChatMessage } from "./llm-wrapper";

interface GameState {
  hp: number;
  maxHp: number;
  characterName: string;
  trait: string;
  inventory: string[];
  setting: string;
  currentChallenge: string;
  previousActions: string[];
  notableEvents: string[];
  challengesCompleted: number;
  turnNumber: number;
  escapeChance: number;
  gamePhase: "intro" | "name" | "trait" | "playing" | "victory" | "death";
}

export class MurdndApp extends ExperimentBase {
  private gameState: GameState;
  private llm: LLMWrapper | null = null;
  private isWaitingForInput: boolean = false;
  private messages: ChatMessage[] = [];

  constructor() {
    const config: ExperimentConfig = {
      id: "murdnd",
      title: "MURD&D",
      tagline: "Survive. Escape. Or Die Trying.",
    };
    super(config);
    this.gameState = this.initializeGameState();
  }

  private initializeGameState(): GameState {
    return {
      hp: 20,
      maxHp: 20,
      characterName: "",
      trait: "",
      inventory: [],
      setting: "",
      currentChallenge: "",
      previousActions: [],
      notableEvents: [],
      challengesCompleted: 0,
      turnNumber: 0,
      escapeChance: 5,
      gamePhase: "intro",
    };
  }

  async init(llm: LLMWrapper): Promise<void> {
    this.llm = llm;
    this.setupUI();
    this.startGame();
  }

  protected setupContent(): void {
    // Required by ExperimentBase, but we handle setup in setupUI
  }

  private setupUI(): void {
    const container = document.getElementById("murdnd-container");
    if (!container) return;

    container.innerHTML = `
      <div id="murdnd-terminal" class="murdnd-terminal">
        <div id="murdnd-status" class="murdnd-status"></div>
        <div id="murdnd-output" class="murdnd-output"></div>
        <div id="murdnd-input-line" class="murdnd-input-line">
          <span class="murdnd-prompt">></span>
          <input type="text" id="murdnd-input" class="murdnd-input" maxlength="50" />
        </div>
      </div>
    `;

    const input = document.getElementById("murdnd-input") as HTMLInputElement;
    input?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.handleInput();
      }
    });

    // Focus input when clicking anywhere on terminal
    const terminal = document.getElementById("murdnd-terminal");
    terminal?.addEventListener("click", () => {
      input?.focus();
    });

    // Handle mobile keyboard and viewport changes
    if (window.innerWidth <= 768) {
      const handleViewportChange = () => {
        const visualViewport = (window as any).visualViewport;
        const inputLine = document.getElementById("murdnd-input-line");
        const output = document.getElementById("murdnd-output");

        if (visualViewport && inputLine) {
          // Calculate keyboard height
          const keyboardHeight = window.innerHeight - visualViewport.height;

          // Move input line above keyboard
          if (keyboardHeight > 0) {
            // Keyboard is visible
            inputLine.style.bottom = `${keyboardHeight}px`;
            if (output) {
              // Adjust output padding to account for keyboard
              output.style.paddingBottom = `${keyboardHeight + 60}px`;
              // Scroll to bottom to show latest content
              setTimeout(() => {
                output.scrollTop = output.scrollHeight;
              }, 100);
            }
          } else {
            // Keyboard is hidden
            inputLine.style.bottom = "";
            if (output) {
              output.style.paddingBottom = "60px";
            }
          }
        }
      };

      // Listen for viewport changes
      const visualViewport = (window as any).visualViewport;
      if (visualViewport) {
        visualViewport.addEventListener("resize", handleViewportChange);
        visualViewport.addEventListener("scroll", handleViewportChange);
      }

      // Also handle focus events as backup
      input?.addEventListener("focus", () => {
        setTimeout(handleViewportChange, 100);
      });

      input?.addEventListener("blur", () => {
        setTimeout(handleViewportChange, 100);
      });
    }
  }

  private startGame(): void {
    this.clearTerminal();
    this.gameState = this.initializeGameState();
    this.gameState.gamePhase = "intro";
    this.messages = [];
    this.updateInitialStatus();

    this.addLine("MURD&D - A Survival Text Adventure");
    this.addLine("=====================================");
    this.addLine("");
    this.addLine("You will wake in a hostile place with no memory.");
    this.addLine("Your only goal: survive and escape.");
    this.addLine("");
    this.addLine("Face challenges. Make choices. Find items.");
    this.addLine("Each success increases your chance of escape.");
    this.addLine("Failure brings pain. Death is permanent.");
    this.addLine("");
    this.addLine("Press ENTER to begin...");

    this.isWaitingForInput = true;
  }

  private handleInput(): void {
    const input = document.getElementById("murdnd-input") as HTMLInputElement;
    if (!input || !this.isWaitingForInput) return;

    const value = input.value.trim();

    // Echo the input
    if (value) {
      this.addLine(`> ${value}`);
    }

    input.value = "";

    switch (this.gameState.gamePhase) {
      case "intro":
        this.gameState.gamePhase = "name";
        this.addLine("");
        this.addLine("What is your name?");
        break;

      case "name":
        if (value) {
          this.gameState.characterName = value.slice(0, 20); // Limit name length
          this.gameState.gamePhase = "trait";
          this.addLine("");
          this.addLine(`Hello, ${this.gameState.characterName}.`);
          this.addLine("");
          this.addLine("Are you strong, fast, or lucky?");
        } else {
          this.addLine("Please enter your name.");
        }
        break;

      case "trait":
        this.gameState.trait = value.toLowerCase();

        this.gameState.gamePhase = "playing";
        this.isWaitingForInput = false;
        this.beginAdventure();
        break;

      case "playing":
        if (value) {
          this.isWaitingForInput = false;
          this.processAction(value);
        }
        break;

      case "victory":
      case "death":
        this.startGame();
        break;
    }
  }

  private async beginAdventure(): Promise<void> {
    this.clearTerminal();
    this.addLine("Building world...");
    this.addLine("");
    await this.generateSetting();
    this.updateStatusLine();
    await this.generateInitialSituation();
    await this.generateChallenge();
    this.isWaitingForInput = true;
  }

  private traitGuidance(): string {
    const t = (this.gameState.trait || "").toLowerCase();
    if (t.includes("strong")) {
      return "STRONG — physical force succeeds more easily: lifting, breaking, bashing, shoving, holding doors, snapping bonds. The player is unusually powerful for their size. Lean into actions that exploit raw strength.";
    }
    if (t.includes("fast")) {
      return "FAST — speed and reflexes succeed more easily: dodging, sprinting, climbing, snatching, sleight of hand, surprise strikes. Penalize sluggish or careful approaches; reward decisive quick action.";
    }
    if (t.includes("lucky")) {
      return "LUCKY — improbable breaks happen more often: convenient finds, near-misses, an enemy stumbling, a rope holding. Unlikely-but-believable outcomes are fair game when the player commits to a plan.";
    }
    return "(no special trait — outcomes follow ordinary realism)";
  }

  private buildSystemPrompt(): string {
    const inv =
      this.gameState.inventory.length > 0
        ? this.gameState.inventory.join(", ")
        : "nothing";
    const events =
      this.gameState.notableEvents.length > 0
        ? this.gameState.notableEvents.slice(-15).map((e) => `- ${e}`).join("\n")
        : "- (none yet)";
    const hpPct = Math.round((this.gameState.hp / this.gameState.maxHp) * 100);
    const hpStatus =
      hpPct >= 75
        ? "uninjured"
        : hpPct >= 40
          ? "wounded"
          : hpPct > 0
            ? "BADLY WOUNDED — near death"
            : "dead";
    return [
      "You are the narrator and game master of MURD&D, a terse, dangerous survival text adventure.",
      "Write in second person singular (you wake, you see). 2-4 sentences per turn unless asked.",
      "",
      "CONTINUITY RULES — these are critical:",
      "- React to EXACTLY what the player typed. Quote or echo specific words/objects from their action.",
      "- Reference prior events, items collected, and injuries from the event log below.",
      "- If the player tries something they already did, acknowledge it and vary the outcome.",
      "- Items in the inventory should actually help when used in fitting ways.",
      "- The trait below is a strong modifier on whether risky actions succeed; respect it.",
      "- HP status colors the tone: a near-death character grunts, stumbles, struggles. An uninjured one moves freely.",
      "",
      "When the player attempts an action, you MUST evaluate honestly and respond in this EXACT format:",
      "OUTCOME: SUCCESS or FAIL",
      "ITEM: <short item name if a useful item is gained on success, otherwise NONE>",
      "NARRATIVE: <2-4 sentences in second person describing the result, referencing the action and prior events.>",
      "",
      "Bad ideas should fail. Clever uses of trait/inventory/prior knowledge should succeed. The world is dangerous; default to skepticism.",
      "",
      "=== CHARACTER ===",
      `Name: ${this.gameState.characterName || "unnamed"}`,
      `Trait: ${this.gameState.trait || "none"}`,
      `Trait meaning: ${this.traitGuidance()}`,
      `HP: ${this.gameState.hp}/${this.gameState.maxHp} (${hpStatus})`,
      `Inventory: ${inv}`,
      "",
      "=== WORLD ===",
      `Setting: ${this.gameState.setting || "(unset)"}`,
      `Current threat: ${this.gameState.currentChallenge || "(none yet)"}`,
      `Turn: ${this.gameState.turnNumber}, challenges overcome: ${this.gameState.challengesCompleted}, escape chance: ${this.gameState.escapeChance}%`,
      "",
      "=== RECENT EVENTS (most recent last) ===",
      events,
    ].join("\n");
  }

  private async narrate(
    userPrompt: string,
    opts: { persist?: boolean; maxTokens?: number } = {},
  ): Promise<string> {
    if (!this.llm) throw new Error("LLM not initialized");
    const persist = opts.persist ?? false;
    const maxTokens = opts.maxTokens ?? 600;
    const system: ChatMessage = {
      role: "system",
      content: this.buildSystemPrompt(),
    };
    const user: ChatMessage = { role: "user", content: userPrompt };
    const response = await this.llm.chat(
      [system, ...this.messages, user],
      maxTokens,
    );
    if (persist) {
      this.messages.push(user);
      this.messages.push({ role: "assistant", content: response });
      // Keep last 12 player exchanges = 24 messages.
      if (this.messages.length > 50) {
        this.messages = this.messages.slice(-50);
      }
    }
    return response;
  }

  private async generateSetting(): Promise<void> {
    if (!this.llm) return;

    const themes = [
      "sci-fi",
      "fantasy",
      "horror",
      "thriller",
      "historical",
      "post-apocalyptic",
    ];
    const randomTheme = themes[Math.floor(Math.random() * themes.length)];

    try {
      const response = await this.narrate(
        `[GM bookkeeping — do not treat as a player action] You wake trapped in a ${randomTheme} setting. Reply with ONLY a single short sentence describing the place. Examples: "Abandoned space station orbiting a dying star." "Flooded subway tunnel, water rising." "Medieval tower, stones crumbling."`,
        { persist: false, maxTokens: 200 },
      );
      this.gameState.setting = response.trim();
    } catch (error) {
      this.gameState.setting = "Abandoned facility. Emergency lights flicker.";
    }
  }

  private async generateInitialSituation(): Promise<void> {
    if (!this.llm) return;

    try {
      const response = await this.narrate(
        "[GM bookkeeping] Describe waking up in this place with no memory. 2-3 vivid sentences. Use second person. Do not include OUTCOME/ITEM/NARRATIVE tags — just the prose.",
        { persist: false, maxTokens: 400 },
      );
      this.addLine(this.gameState.setting);
      this.addLine("");
      this.addLine(response);
    } catch (error) {
      this.addLine("You wake. Head throbs. How did you get here?");
    }
  }

  private async generateChallenge(): Promise<void> {
    if (!this.llm) return;

    try {
      const response = await this.narrate(
        `[GM bookkeeping] Introduce a NEW obstacle (monster, trap, puzzle, barrier) that fits the setting and feels distinct from anything in the player's recent history. 2-3 sentences of pure prose, no tags. Avoid repeating monsters/traps the player has already faced.`,
        { persist: false, maxTokens: 500 },
      );
      this.gameState.currentChallenge = response.trim();
      this.addLine("");
      this.addLine(this.gameState.currentChallenge);
      this.addLine("");
      this.addLine("What do you do?");
    } catch (error) {
      this.gameState.currentChallenge = "A locked door blocks your path.";
      this.addLine(this.gameState.currentChallenge);
    }
  }

  private async processAction(action: string): Promise<void> {
    if (!this.llm) return;

    this.gameState.turnNumber++;

    // Player message is the raw action, nothing else. The format/rules live in the system prompt.
    try {
      const response = await this.narrate(action, { persist: true, maxTokens: 700 });

      const outcomeMatch = response.match(/OUTCOME:\s*(SUCCESS|FAIL)/i);
      const itemMatch = response.match(/ITEM:\s*([^\n]+)/i);
      const narrativeMatch = response.match(/NARRATIVE:\s*([\s\S]+)/i);

      const isSuccess = outcomeMatch
        ? outcomeMatch[1].toUpperCase() === "SUCCESS"
        : false;
      const rawItem = itemMatch ? itemMatch[1].trim() : "";
      const item =
        rawItem && !/^none$/i.test(rawItem) ? rawItem.slice(0, 30) : null;
      const narrative = (narrativeMatch ? narrativeMatch[1] : response).trim();

      this.addLine("");
      this.addLine(narrative);

      await this.processOutcome(isSuccess, item);

      if (this.gameState.hp > 0 && this.gameState.gamePhase === "playing") {
        await this.checkForEscape();
      }
    } catch (error) {
      this.addLine("Your attempt yields uncertain results...");
    }
  }

  private logEvent(text: string): void {
    this.gameState.notableEvents.push(`Turn ${this.gameState.turnNumber}: ${text}`);
    if (this.gameState.notableEvents.length > 30) {
      this.gameState.notableEvents = this.gameState.notableEvents.slice(-30);
    }
  }

  private async processOutcome(
    isSuccess: boolean,
    item: string | null,
  ): Promise<void> {
    const threatSummary = this.gameState.currentChallenge
      .split(/[.!?]/)[0]
      .trim()
      .slice(0, 80);

    if (isSuccess) {
      this.gameState.challengesCompleted++;
      this.gameState.escapeChance = Math.min(
        50,
        5 + this.gameState.challengesCompleted * 1,
      );

      if (item) {
        this.gameState.inventory.push(item);
        this.addLine(`[+${item}]`);
        this.logEvent(`Survived "${threatSummary}", obtained ${item}`);
      } else {
        this.logEvent(`Survived "${threatSummary}"`);
      }
    } else {
      const damage = Math.floor(Math.random() * 4) + 2;
      this.gameState.hp = Math.max(0, this.gameState.hp - damage);
      this.addLine(`[-${damage} HP]`);
      this.logEvent(
        `Hurt by "${threatSummary}" (-${damage} HP, now ${this.gameState.hp}/${this.gameState.maxHp})`,
      );

      if (this.gameState.hp <= 0) {
        this.logEvent("Died");
        this.handleDeath();
        return;
      }
    }

    this.updateStatusLine();
  }

  private async checkForEscape(): Promise<void> {
    const roll = Math.random() * 100;

    if (roll < this.gameState.escapeChance) {
      await this.generateEscape();
      this.handleVictory();
    } else {
      await this.generateChallenge();
      this.isWaitingForInput = true;
    }
  }

  private async generateEscape(): Promise<void> {
    if (!this.llm) return;

    try {
      const response = await this.narrate(
        "[GM bookkeeping] The player spots a viable escape route! Describe the escape in 2-3 dramatic sentences using second person. Pure prose, no tags. Reference items they collected and obstacles they overcame.",
        { persist: false, maxTokens: 400 },
      );
      this.addLine("");
      this.addLine(response);
    } catch (error) {
      this.addLine("Light! You stumble toward an exit, gasping fresh air.");
    }
  }

  private handleVictory(): void {
    this.gameState.gamePhase = "victory";
    this.addLine("");
    this.addLine("=====================================");
    this.addLine("ESCAPED!");
    this.addLine(
      `${this.gameState.characterName} survived ${this.gameState.challengesCompleted} challenges.`,
    );
    this.addLine("");
    this.addLine("Press ENTER to play again...");
    this.isWaitingForInput = true;
  }

  private handleDeath(): void {
    this.gameState.gamePhase = "death";
    this.addLine("");
    this.addLine("=====================================");
    this.addLine("YOU DIED");
    this.addLine(
      `${this.gameState.characterName} survived ${this.gameState.challengesCompleted} challenges.`,
    );
    this.addLine("");
    this.addLine("Press ENTER to play again...");
    this.isWaitingForInput = true;
  }

  private updateStatusLine(): void {
    const status = document.getElementById("murdnd-status");
    if (status && this.gameState.characterName) {
      status.textContent = `[${this.gameState.characterName} - HP: ${this.gameState.hp}/${this.gameState.maxHp}]`;
      status.style.display = "block";
    }
  }

  private updateInitialStatus(): void {
    const status = document.getElementById("murdnd-status");
    if (status) {
      status.textContent = "[MURD&D - Initializing...]";
      status.style.display = "block";
    }
  }

  private addLine(text: string): void {
    const output = document.getElementById("murdnd-output");
    if (!output) return;

    const line = document.createElement("div");
    line.className = "murdnd-line";
    line.textContent = text;
    output.appendChild(line);

    // Scroll to bottom - ensure the latest text is visible
    requestAnimationFrame(() => {
      output.scrollTop = output.scrollHeight;
    });
  }

  private clearTerminal(): void {
    const output = document.getElementById("murdnd-output");
    if (output) {
      output.innerHTML = "";
    }
    // Don't hide status anymore - it should always be visible
  }

  activate(): void {
    const container = document.getElementById("murdnd-container");
    if (container) container.style.display = "block";

    // Focus input
    const input = document.getElementById("murdnd-input") as HTMLInputElement;
    input?.focus();
  }

  deactivate(): void {
    const container = document.getElementById("murdnd-container");
    if (container) container.style.display = "none";
  }
}
